import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Flex, Button, message, Skeleton, Typography, Select } from 'antd';

import { CustomDatePicker } from '../../components/CustomDatePicker';

import apis from '../../services/Apis';

import { table } from './struct';
import { SecureGet } from '../../services/axiosCall';

import { formRules } from '../../const/formRules';
import { TableDescription } from '../../components/TableDescription';
import CommentModal from '../../components/Modal/CommentModal';
import useAdminEvaluation from '../../hooks/useAdminEvaluation';


const { Title, Paragraph } = Typography

const withTableController = (Table) => {
    // eslint-disable-next-line react/display-name
    return ({ currentApas, tableNo, dataConverter, menu }) => {
        const { id: facultyId } = useParams();
        const apa = currentApas?.length > 0 && currentApas?.find((item) => item.form_no === tableNo) || {};
        const [messageApi, contextHolder] = message.useMessage();

        const _static = useSelector((state) => state.static);
        const { staticData } = _static;

        const { evaluation_status: evaluationStatus } = staticData;


        const user = useSelector((state) => state.user);
        const { userDetails } = user;

        const [loading, setLoading] = useState(false);
        const handleLoading = (loading) => setLoading(loading);

        const [apaShortDes, setApaShortDes] = useState(null);
        const {
            form_no: formNo,
            form_name: formName,
            max_point: maxPoints
        } = apaShortDes || {};
        const handleApaShortDes = (des) => setApaShortDes(des);


        const [apaRules, setApaRules] = useState(null);
        const handleApaRules = (rules) => setApaRules(rules);


        // Default to the year passed in from the Faculty Details page (menu.year)
        // so this panel doesn't silently reset to last year regardless of what
        // was selected there. Falls back to last year if none was passed.
        const currentDate = new Date();
        const fallbackYear = currentDate.getFullYear() - 1;
        const [selectedYear, setSelectedYear] = useState(
            Number(menu?.year) || Number(fallbackYear)
        );
        const handleSelectedYear = (_, yearString) =>
            setSelectedYear(Number(yearString));

        const { toTableData, toRequestData } = dataConverter || {};

        const fetchApaShortDes = (id) => {
            handleApaShortDes(null);
            handleLoading(true);

            SecureGet({ url: `${apis.FORM_LIST}/${id}` })
                .then((response) => {
                    handleApaShortDes(response.data);
                    handleLoading(false);
                })
                .catch(() => {
                    handleLoading(false);
                });
        };

        const {
            planing,
            fetchLoading,
            handlePlaning,
            evaluatePlaning,
            evaluateLoading,
            cumulativeApaPoints,
            status,
            semesterComments
        } = useAdminEvaluation({
            url: apis.EVALUATION_ADMIN,
            formId: apa.id,
            userDetails: userDetails.user_id,
            rowUser: facultyId,
            selectedYear: selectedYear,
            messageApi: messageApi,
            convertToTableData: toTableData,
            convertToRequestData: toRequestData
        });

        const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
        const handleCommentModalVisible = (el) => setIsCommentModalVisible(el);
        const [comment, setComment] = useState('');
        const [selectedStatus, setSelectedStatus] = useState(status);
        const handleSelectedStatus = (el) => setSelectedStatus(el)

        const handleStatusChange = (value) => {
            setSelectedStatus(value); // Update the selected status state

            if (value === 2 || value === 4) {
                handleCommentModalVisible(true);
            } else {
                // Status is persisted by the existing Submit action.
            }
        };

        const handleCommentSubmit = () => {
            if (comment.trim()) {
                handleCommentModalVisible(false);
            } else {
                alert("Comment is mandatory for Rejected and Requested statuses.");
            }
        };

        const handleCancel = () => {
            handleCommentModalVisible(false);
            handleSelectedStatus(status); // Reset to the previous status if modal is canceled
        };

        useEffect(() => {
            if (menu?.currentApaId) fetchApaShortDes(menu?.currentApaId);
        }, [menu]);

        useEffect(() => {
            if (apaShortDes) {
                const _formRules = formRules.find((item) => formNo === item.id);
                const { rules } = _formRules || {};
                handleApaRules(rules);
            }
        }, [apaShortDes]);

        useEffect(() => {
            handleSelectedStatus(status)
        }, [status])

        return (
            <Card {...table.wrapper}>
                {contextHolder}

                {
                    apaShortDes && !loading &&
                    <TableDescription
                        formNo={menu?.currentMenuId}
                        formName={formName}
                        maxPoints={maxPoints}
                        rules={apaRules}
                        cumulativeApaPoints={cumulativeApaPoints}
                    />
                }
                <br />
                <Flex {...table.header}>
                    <CustomDatePicker
                        selectedYear={selectedYear}
                        handleSelectedYear={handleSelectedYear}
                    />

                    <Flex justify='flex-end'>
                        <Select
                            disabled={fetchLoading || !planing?.length}
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            style={{ width: 120 }}
                        >
                            {evaluationStatus?.length > 0 && evaluationStatus?.map((e) => (
                                <Select.Option key={e.value} value={e.value}>
                                    {e.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Flex>
                </Flex>

                {semesterComments && (
                    <Paragraph>
                        <strong>Comment:</strong> {semesterComments}
                    </Paragraph>
                )}

                {planing && !fetchLoading ?
                    planing?.length > 0 ?
                        <Table
                            planing={planing}
                            handlePlaning={handlePlaning}
                            status={status}
                        />
                        : <Title level={4} style={{ textAlign: 'center', marginTop: 20 }}>
                            No evaluation data available.
                        </Title>
                    :
                    <Skeleton loading={fetchLoading} active />
                }

                <br />
                <Flex justify='end'>
                    <Button
                        {...table.saveButton}
                        loading={evaluateLoading}
                        disabled={fetchLoading || !planing?.length}
                        onClick={() => {
                            if ((selectedStatus === 2 || selectedStatus === 4) && !comment.trim()) {
                                alert("Comment is mandatory for Rejected and Requested statuses.");
                                handleCommentModalVisible(true);
                            } else {
                                evaluatePlaning(selectedStatus, comment);
                            }
                        }}
                    >
                        Submit
                    </Button>

                </Flex>

                <CommentModal
                    isCommentModalVisible={isCommentModalVisible}
                    handleCommentSubmit={handleCommentSubmit}
                    handleCancel={handleCancel}
                    comment={comment}
                    setComment={setComment}
                />

            </Card>
        );
    };
};

export default withTableController;